import React, { useEffect, useRef } from "react";
import { client, serverUrl } from "../../config";

const getPlyrEmbedUrl = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com"))
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    if (u.hostname.includes("youtu.be"))
      return `https://www.youtube.com/embed${u.pathname}`;
    if (u.hostname.includes("vimeo.com"))
      return `https://player.vimeo.com/video${u.pathname}`;
    return url;
  } catch {
    return url;
  }
};

const resolveVideoSource = (videoSrc) => {
  if (client === "companyapp") {
    if (videoSrc.field_media_oembed_video) {
      return {
        src: getPlyrEmbedUrl(videoSrc.field_media_oembed_video),
        isRemote: true,
      };
    }
    if (videoSrc.file_access_token) {
      return {
        src: `${serverUrl}/shared/files/${videoSrc.filename}?access_token=${videoSrc.file_access_token}`,
        isRemote: false,
      };
    }
    return {
      src: `${serverUrl}/api/v2/assets?mid=${videoSrc.mid}`,
      isRemote: false,
    };
  }

  return {
    src: `${serverUrl}${
      videoSrc.field_media_video_file
        ? videoSrc.field_media_video_file
        : videoSrc.field_video
    }`,
    isRemote: false,
  };
};

export default function VideoPlayer({ post, selectedMedia, onOpenMediaLibrary }) {
  const videoRef = useRef(null);
  const plyrInstance = useRef(null);

  const videoSrc = selectedMedia ?? post;
  const { src: videoSource, isRemote } = resolveVideoSource(videoSrc);
  const autoplay = client === "equans";

  // Initialise (or re-initialise) Plyr whenever the resolved source changes
  useEffect(() => {
    if (isRemote || !videoRef.current) return;

    // Destroy any existing instance first
    if (plyrInstance.current) {
      try {
        plyrInstance.current.destroy();
      } catch (_) {
        // already destroyed
      }
      plyrInstance.current = null;
    }

    plyrInstance.current = new window.Plyr(videoRef.current, {
      controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "fullscreen",
      ],
    });

    return () => {
      if (plyrInstance.current) {
        try {
          plyrInstance.current.destroy();
        } catch (_) {
          // already destroyed
        }
        plyrInstance.current = null;
      }
    };
  }, [videoSource, isRemote]);

  return (
    <div className="post-video-container-redactor" style={{ position: "relative" }}>

      {isRemote ? (
        <div className="plyr__video-embed post-video-embed">
          <iframe
            key={videoSource}
            src={videoSource}
            allowFullScreen
            allow="autoplay; encrypted-media"
            title={post.title}
          />
        </div>
      ) : (
        /*
         * key={videoSource} on this wrapper — not the <video> itself — forces
         * React to remount the subtree when the source changes without fighting
         * Plyr over the inner DOM nodes.
         */
        <div key={videoSource}>
          <video
            ref={videoRef}
            data-id={post.id}
            controls
            autoPlay={autoplay}
            className="post-video plyr"
          >
            <source src={videoSource} />
          </video>
        </div>
      )}
    </div>
  );
}